import DesignButton from 'components/button/DesignButton';
import useUrl from 'core/hooks/useUrl';
import useItineraryDataStore from 'core/hooks/useItineraryDataStore';
import { useState } from 'react';
import useEventsDataStore from 'core/hooks/useEventsDataStore';
import InformationalMessage from 'components/informationalMessage/InformationalMessage';
import { TrafficUpdatesGrpcServiceClient } from 'core/grpc/TrafficUpdatesServiceClientPb';
import useCourierPositionsDataStore from 'core/hooks/useCourierPositionsDataStore';
import EventsList from 'components/eventsList/EventsList';
import MapSection from 'components/mapSection';
import DailyProgressItem from 'components/dailyProgress/DailyProgressItem';
import useDailyProgressCardDataStore from 'core/hooks/useDailyProgressDataStore';
import useAppDispatch from 'core/hooks/useAppDispatch';
import { setCoordinates, setZoomLevel } from 'pages/itinerary/MapSlice';
import WithLoadingSpinner from 'components/withLoadingSpinner/WithLoadingSpinner';
import { BackIcon } from 'components/svgs';
import ItineraryStartTime from '../itineraryStartTime/ItineraryStartTime';
import PauseIntervals from '../pauseIntervals/PauseIntervals';
import DeliveryTimes from '../deliveryTimes/DeliveryTimes';
import ItineraryWarnings from '../itineraryWarnings/ItineraryWarnings';
import ItinerarySearch from '../itinerarySearch/ItinerarySearch';
import ItineraryVersionItem from '../itineraryVersionItem/ItineraryVersionItem';
import useAgencyZonesDataStore from '../../core/hooks/useAgencyZones';

const trafficUpdatesServiceUrl = `${process.env.REACT_APP_HISTORICAL_DATA_GRPC_URL}`;
const client = new TrafficUpdatesGrpcServiceClient(trafficUpdatesServiceUrl);

const ItineraryViewComponent = () => {
  const dispatch = useAppDispatch();
  const { paramValue: dateParamValue, setParamValue: setDateParamValue } =
    useUrl<string>('Date');
  const { paramValue: courierParamValue, setParamValue: setCourierParamValue } =
    useUrl<number | null>('CourierId');
  const { paramValue: agencyParamValue } =
    useUrl<string>('Agency');
  const [selectedItineraryId, setSelectedItineraryId] = useState();
  const [filterTags, setFilterTags] = useState<string[]>([]);

  const {
    itineraries,
    loadingItineraries,
    refresh: refreshItineraries,
  } = useItineraryDataStore(dateParamValue, courierParamValue!);

  const {
    dailyProgress,
    deliveryStatistics,
    refresh: refreshDailyProgress,
  } = useDailyProgressCardDataStore(dateParamValue, courierParamValue);

  const {
    loadingEvents,
    events,
    refresh: refreshEvents,
  } = useEventsDataStore(selectedItineraryId);

  const { courierPositions } = useCourierPositionsDataStore(
    dateParamValue,
    courierParamValue,
    client,
  );


  const {
    agencyZones
  } = useAgencyZonesDataStore(agencyParamValue);

  const handleDateChanged = (e: any) => {
    const { value } = e.target;
    setDateParamValue(value);
  };

  const handleCourierChanged = ({ value }: any) => {
    setCourierParamValue(value);
  };

  const handleBackButtonClick = () => {
    setCourierParamValue(null);
  };

  const handleEventsListRefresh = () => {
    refreshEvents();
    refreshDailyProgress();
  };

  const handleItineraryVersionClick = (itineraryVersionId: any) => {
    setSelectedItineraryId(itineraryVersionId);
    const firstGeocodedStop = itineraries
      .find((x) => x.id === itineraryVersionId)
      ?.itineraryStops.find((x) => x.latitude !== null && x.longitude !== null);

    if (firstGeocodedStop) {
      dispatch(
        setCoordinates({
          latitude: firstGeocodedStop.latitude!,
          longitude: firstGeocodedStop.longitude!,
        }),
      );
      dispatch(setZoomLevel(13));
    }
  };

  const eventsFilterChangedHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      setFilterTags([...filterTags, event.target.value]);
    } else {
      setFilterTags(
        filterTags.filter((filterTag) => filterTag !== event.target.value),
      );
    }
  };

  const selectedItinerary = itineraries.find(
    (x) => x.id === selectedItineraryId,
  );

  const filteredEvents = events.filter((event) =>
    filterTags.length > 0
      ? filterTags.some((filterTag) => event.eventType === filterTag)
      : events,
  );

  return (
    <WithLoadingSpinner isLoading={loadingItineraries || loadingEvents}>
      <div className="h-full w-5/12 overflow-y-auto px-4 pt-2 pb-4">
        <div className="h-full pb-4">
          <div>
            <DesignButton
              onClickHandler={handleBackButtonClick}
              className="mb-6 flex w-fit items-center gap-2 px-4 py-1"
            >
              <BackIcon className="dark:fill-white" />
              <span>Back</span>
            </DesignButton>
            <ItinerarySearch
              onRefreshClicked={refreshItineraries}
              onDateChanged={handleDateChanged}
              onCourierChange={handleCourierChanged}
            />
          </div>

          {!itineraries.length ? (
            <InformationalMessage message="There are no itineraries for today" />
          ) : (
            <div className="mt-4">
              <ul>
                {itineraries.map((itinerary) => (
                  <ItineraryVersionItem
                    key={itinerary.id}
                    itineraryVersion={itinerary}
                    selectedItineraryId={selectedItineraryId}
                    itineraryVersionClickHandler={handleItineraryVersionClick}
                  />
                ))}
              </ul>
            </div>
          )}
          <div>
            {selectedItinerary?.startingTime && (
              <ItineraryStartTime startTime={selectedItinerary?.startingTime} />
            )}

            {selectedItinerary && (
              <div className="flex">
                <div className="w-1/2">
                  {selectedItinerary?.pauseIntervals && (
                    <PauseIntervals
                      pauseIntervals={selectedItinerary?.pauseIntervals}
                    />
                  )}
                </div>
                <div className="w-1/2">
                  {selectedItinerary && (
                    <DeliveryTimes itineraryVersion={selectedItinerary} />
                  )}
                </div>
              </div>
            )}

            {selectedItinerary?.itineraryWarnings && (
              <ItineraryWarnings
                itineraryWarnings={selectedItinerary.itineraryWarnings}
              />
            )}
          </div>
          {selectedItinerary && dailyProgress && (
            <DailyProgressItem
              dailyProgress={dailyProgress}
              deliveryStatistics={
                deliveryStatistics ? [deliveryStatistics] : []
              }
            />
          )}
          {selectedItinerary && (
            <div>
              <EventsList
                events={filteredEvents}
                selectedFilters={filterTags}
                filterHandler={eventsFilterChangedHandler}
              />
              <div className="mx-auto mb-4 w-fit">
                <DesignButton
                  onClickHandler={handleEventsListRefresh}
                  disabled={!selectedItinerary}
                >
                  Refresh
                </DesignButton>
              </div>
              <div className="h-2" />
            </div>
          )}
        </div>
      </div>
      <div className="h-full w-7/12">
        <MapSection
          itinerary={selectedItinerary}
          events={filteredEvents}
          liveTrackingCoordinates={courierPositions}
          agencyZones={agencyZones}
        />
      </div>
    </WithLoadingSpinner>
  );
};
export default ItineraryViewComponent;
